import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Smartphone, Menu, X, Clock, ChevronDown } from "lucide-react";
import * as Lucide from "lucide-react";
import { CLINIC, FEATURED_SERVICES } from "../../lib/clinic";
import { Button } from "../ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import SocialIcons from "./SocialIcons";
import { trackEvent } from "../../lib/analytics";

const SECTIONS = [
  { id: "servicos", key: "nav.servicos" },
  { id: "tecnologia", key: "nav.tecnologia" },
  { id: "porque-nos", key: "nav.sobre" },
  { id: "testemunhos", key: "nav.testemunhos" },
  { id: "marcacao", key: "nav.marcacao" },
  { id: "contactos", key: "nav.contactos" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function computeTodayStatus(t) {
  const now = new Date();
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  if (day === 0 || day === 6) return { open: false, label: t("status.closedToday") };
  const close2 = day <= 3 ? 19 * 60 : 18 * 60;
  const open1 = 9 * 60 + 30, close1 = 13 * 60;
  const open2 = day <= 3 ? 15 * 60 : 14 * 60;
  const closeTime = day <= 3 ? "19:00" : "18:00";
  const open2Time = day <= 3 ? "15:00" : "14:00";
  if (mins < open1) return { open: false, label: t("status.opensAt", { time: "09:30" }) };
  if (mins < close1) return { open: true, label: t("status.openUntil", { time: "13:00" }) };
  if (mins < open2) return { open: false, label: t("status.reopensAt", { time: open2Time }) };
  if (mins < close2) return { open: true, label: t("status.openUntil", { time: closeTime }) };
  return { open: false, label: t("status.closedToday") };
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [status, setStatus] = useState(() => computeTodayStatus(t));
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (id) => {
    if (location.pathname === "/") {
      scrollTo(id);
    } else {
      navigate(`/#${id}`);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    const interval = setInterval(() => setStatus(computeTodayStatus(t)), 60_000);
    return () => { window.removeEventListener("scroll", onScroll); clearInterval(interval); };
  }, [t]);

  useEffect(() => { setStatus(computeTodayStatus(t)); }, [i18n.language, t]);

  const mobileNumber = CLINIC.whatsappRaw.startsWith("+") ? CLINIC.whatsappRaw : `+${CLINIC.whatsappRaw}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 border-b ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-slate-200/80 shadow-[0_2px_20px_rgba(15,62,104,0.06)]" : "bg-white border-slate-100"
      }`}
    >
      <div className="hidden lg:block border-b border-slate-200/70" style={{ backgroundColor: "#F8F8F8" }}>
        <div className="container-icb flex items-center justify-between gap-5 h-9 text-xs">
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
          </div>
          <div className="flex items-center gap-5">
            <a href={`tel:${CLINIC.phoneRaw}`} onClick={() => trackEvent("click_phone", { source: "header_topbar_fixo" })} data-testid="header-phone-fixo" className="inline-flex items-center gap-1.5 text-brand-500 hover:text-gold-500 font-semibold transition-colors">
              <Phone size={13} strokeWidth={2.5} /> {CLINIC.phone}
            </a>
            <span className="text-slate-300">·</span>
            <a href={`tel:${mobileNumber}`} onClick={() => trackEvent("click_phone", { source: "header_topbar_movel" })} data-testid="header-phone-movel" className="inline-flex items-center gap-1.5 text-brand-500 hover:text-gold-500 font-semibold transition-colors">
              <Smartphone size={13} strokeWidth={2.5} /> {CLINIC.whatsapp}
            </a>
            <span className="text-slate-300">·</span>
            <a href={`mailto:${CLINIC.email}?cc=rececao@institutobarlavento.com`} onClick={() => trackEvent("click_email", { source: "header" })} data-testid="header-email" className="inline-flex items-center gap-1.5 text-brand-500 hover:text-gold-500 font-semibold transition-colors">
              <Lucide.Mail size={13} strokeWidth={2.5} /> {CLINIC.email}
            </a>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5 text-slate-500" data-testid="header-status">
              <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-slate-300"}`} />
              <Clock size={12} className="text-slate-400" /> {status.label}
            </span>
            <SocialIcons size={28} location="header" className="ml-1" />
          </div>
        </div>
      </div>

      <div className="container-icb flex items-center justify-between gap-4 h-20 lg:h-24">
        <button onClick={() => goToSection("hero")} data-testid="header-logo" className="flex items-center group shrink-0">
          <img src={CLINIC.logo} alt="Instituto Clínico do Barlavento" className="h-14 sm:h-16 lg:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]" />
        </button>

        <nav className="hidden lg:flex items-center">
          {SECTIONS.map((s) => {
            if (s.id === "servicos") {
              return (
                <div
                  key={s.id}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    data-testid="nav-servicos"
                    onClick={() => setServicesOpen((v) => !v)}
                    aria-expanded={servicesOpen}
                    className="inline-flex items-center gap-1.5 px-3.5 xl:px-4 py-2 text-base xl:text-[17px] font-semibold text-brand-500 hover:text-gold-500 transition-colors whitespace-nowrap"
                  >
                    {t(s.key)}
                    <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {servicesOpen && (
                    <div
                      data-testid="services-dropdown"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[320px] z-50"
                    >
                      <div className="bg-white rounded-xl shadow-xl border border-slate-200/80 py-2 animate-fade-in">
                        <p className="px-4 pt-2 pb-2 text-[10px] uppercase tracking-[0.18em] text-gold-500 font-semibold">{t("nav.services.title")}</p>
                        {FEATURED_SERVICES.map((sv) => {
                          const Icon = Lucide[sv.icon] || Lucide.Sparkles;
                          return (
                            <Link
                              key={sv.key}
                              to={`/${sv.slug}`}
                              data-testid={`services-dropdown-${sv.key}`}
                              onClick={() => setServicesOpen(false)}
                              className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                            >
                              <span className="shrink-0 w-9 h-9 rounded-lg bg-brand-50 text-brand-500 inline-flex items-center justify-center group-hover:bg-brand-500 group-hover:text-white transition-colors">
                                <Icon size={18} strokeWidth={1.7} />
                              </span>
                              <span className="flex-1">
                                <span className="block font-serif text-brand-500 text-[15px] leading-tight">{t(`svc4.${sv.key}.name`)}</span>
                                <span className="block text-xs text-slate-500 mt-0.5 leading-snug">{t(`svc4.${sv.key}.desc`).slice(0, 70)}…</span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button key={s.id} data-testid={`nav-${s.id}`} onClick={() => goToSection(s.id)} className="px-3.5 xl:px-4 py-2 text-base xl:text-[17px] font-semibold text-brand-500 hover:text-gold-500 transition-colors whitespace-nowrap">
                {t(s.key)}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 lg:gap-3">
          <div className="lg:hidden"><LanguageSwitcher compact /></div>
          <a href={`tel:${mobileNumber}`} onClick={() => trackEvent("click_phone", { source: "header_mobile_icon" })} data-testid="header-phone-mobile" aria-label="Phone" className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-brand-500 hover:bg-brand-50">
            <Smartphone size={20} strokeWidth={2.2} />
          </a>
          <Button data-testid="header-cta-marcar" onClick={() => goToSection("marcacao")} className="bg-gold-400 hover:bg-gold-500 text-brand-700 font-semibold px-5 lg:px-6 h-11 rounded-lg shadow-md shadow-gold-200 hover:-translate-y-0.5 transition-all">
            {t("cta.marcar")}
          </Button>
          <button aria-label="Menu" data-testid="mobile-menu-toggle" onClick={() => setMenuOpen((v) => !v)} className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-brand-500 hover:bg-slate-100">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200/60" data-testid="mobile-menu">
          <div className="container-icb py-3 flex flex-col">
            {SECTIONS.map((s) => {
              if (s.id === "servicos") {
                return (
                  <div key={s.id} className="border-b border-slate-100">
                    <button
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      data-testid="mobile-nav-servicos"
                      aria-expanded={mobileServicesOpen}
                      className="w-full flex items-center justify-between text-left px-3 py-3 text-base font-semibold text-brand-500 hover:text-gold-500 transition-colors"
                    >
                      <span>{t(s.key)}</span>
                      <ChevronDown size={16} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {mobileServicesOpen && (
                      <div className="pl-3 pb-2" data-testid="mobile-services-dropdown">
                        {FEATURED_SERVICES.map((sv) => (
                          <Link
                            key={sv.key}
                            to={`/${sv.slug}`}
                            data-testid={`mobile-services-${sv.key}`}
                            onClick={() => { setMenuOpen(false); setMobileServicesOpen(false); }}
                            className="block px-3 py-2.5 text-sm text-slate-700 hover:text-gold-500 hover:bg-slate-50 rounded-md transition-colors"
                          >
                            · {t(`svc4.${sv.key}.name`)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button key={s.id} onClick={() => { goToSection(s.id); setMenuOpen(false); }} className="text-left px-3 py-3 text-base font-semibold text-brand-500 hover:text-gold-500 border-b border-slate-100 transition-colors">
                  {t(s.key)}
                </button>
              );
            })}
            <a href={`tel:${CLINIC.phoneRaw}`} onClick={() => trackEvent("click_phone", { source: "header_mobile_menu_fixo" })} className="px-3 py-3 mt-2 inline-flex items-center gap-2 text-brand-500 font-semibold border-b border-slate-100" data-testid="mobile-phone-fixo">
              <Phone size={16} /> {CLINIC.phone}
            </a>
            <a href={`tel:${mobileNumber}`} onClick={() => trackEvent("click_phone", { source: "header_mobile_menu_movel" })} className="px-3 py-3 inline-flex items-center gap-2 text-brand-500 font-semibold" data-testid="mobile-phone-movel">
              <Smartphone size={16} /> {CLINIC.whatsapp}
            </a>
            <Link to="/financiamento" onClick={() => { setMenuOpen(false); trackEvent("click_financing", { source: "header_mobile_menu" }); }} data-testid="mobile-financing-link" className="px-3 py-3 border-t border-slate-100 inline-flex items-center gap-2 text-brand-500 font-semibold">
              <Lucide.CreditCard size={16} /> Financiamento
            </Link>
            <p className="px-3 pb-2 text-xs text-slate-500 inline-flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${status.open ? "bg-emerald-500" : "bg-slate-300"}`} /> {status.label}
            </p>
            <div className="px-3 pt-3 mt-1 border-t border-slate-100">
              <SocialIcons size={36} location="header_mobile" onItemClick={() => setMenuOpen(false)} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
